import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import { useNavigate } from 'react-router-dom';
import { GanttChart } from '../gantt/GanttChart';
import { TrackProgressBar } from '../TrackProgressBar';
import {
  useDependencies,
  useMilestoneLinks,
  useProjectDashboard,
  useSchedule,
} from '../../hooks/useOps';
import { formatDate, humanizeStatus, milestoneEffectiveDate, statusColor } from '../../lib/format';
import { topRunningTasks } from '../../lib/runningTasks';
import { qualificationPct } from './quality';

const STATUS_ORDER: Record<string, number> = {
  late: 0,
  at_risk: 1,
  in_progress: 2,
  not_started: 3,
  complete: 4,
};

/** Read-mostly execution overview: progress, timeline, gates, problems, quality. */
export function OverviewTab({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const { data } = useProjectDashboard(projectId);
  const { data: dependencies = [] } = useDependencies(projectId);
  const { data: schedule } = useSchedule(projectId);
  const { data: milestoneLinks = [] } = useMilestoneLinks(projectId);

  if (!data) return null;
  const { tracks, deliveryMilestones, qualifications, overallPct } = data;

  const sortedTracks = [...tracks].sort(
    (a, b) =>
      (STATUS_ORDER[a.computedStatus ?? a.status] ?? 5) -
      (STATUS_ORDER[b.computedStatus ?? b.status] ?? 5),
  );

  // Critical path rendered as a readable list, not just Gantt highlighting.
  const itemById = new Map(
    tracks.flatMap((t) => (t.items ?? []).map((i) => [i.id, { item: i, track: t }] as const)),
  );
  const criticalItems = (schedule?.criticalPath ?? [])
    .map((id) => itemById.get(id))
    .filter(Boolean) as Array<{ item: { name: string; scheduledEnd?: string | null }; track: { label: string } }>;

  // Top 3 sub-tasks actively being worked — the "what's happening right now" lead.
  const runningTasks = topRunningTasks(
    tracks.map((t) => ({
      label: t.label,
      items: (t.items ?? []).map((i) => ({
        id: i.id,
        name: i.name,
        status: i.computedStatus ?? i.status,
        progressPct: i.progressPct,
        scheduledEnd: i.scheduledEnd,
      })),
    })),
    schedule?.criticalPath ?? [],
  );

  return (
    <Stack gap={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Overall progress</Typography>
          <Stack direction="row" gap={1} alignItems="center">
            <CircularProgress
              variant="determinate"
              value={overallPct}
              size={40}
              color={overallPct === 100 ? 'success' : 'primary'}
            />
            <Typography variant="h5" fontWeight={700}>
              {overallPct}%
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {runningTasks.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" gap={0.75} alignItems="center" mb={1.5}>
            <BoltIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Running now
            </Typography>
            <Typography variant="caption" color="text.secondary">
              top {runningTasks.length} active sub-task{runningTasks.length === 1 ? '' : 's'}
            </Typography>
          </Stack>
          <Stack gap={1.5}>
            {runningTasks.map((t) => (
              <Box key={t.id}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {t.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {t.trackLabel}
                      {t.scheduledEnd ? ` · due ${formatDate(t.scheduledEnd)}` : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" gap={1} alignItems="center" flexShrink={0}>
                    {t.onCriticalPath && (
                      <Tooltip title="On the critical path — a slip here slips the whole project.">
                        <Chip size="small" variant="outlined" color="warning" label="critical path" />
                      </Tooltip>
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      color={statusColor(t.status)}
                      label={humanizeStatus(t.status)}
                    />
                    <Typography variant="body2" fontWeight={700}>
                      {t.progressPct}%
                    </Typography>
                  </Stack>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={t.progressPct}
                  color={t.status === 'late' ? 'error' : 'primary'}
                  sx={{ mt: 0.75, height: 6, borderRadius: 3 }}
                />
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
          Timeline
        </Typography>
        <GanttChart
          tracks={tracks}
          dependencies={dependencies}
          criticalPath={schedule?.criticalPath ?? []}
          holidays={schedule?.holidays ?? []}
          milestones={deliveryMilestones.map((m) => ({
            id: m.id,
            name: m.name,
            // "graph wins": a linked milestone sits at its engine-computed date.
            date: m.scheduledDate ?? m.dueDate,
            done: (m.computedStatus ?? m.status) === 'complete',
            dueDate: m.dueDate,
            delayReason: m.delayReason,
          }))}
          milestoneLinks={milestoneLinks}
          maxHeight="70vh"
        />
        {criticalItems.length > 0 && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Stack direction="row" gap={0.5} alignItems="center" mb={0.5}>
              <Typography variant="subtitle2" fontWeight={600}>
                Critical path
              </Typography>
              <Tooltip title="Tasks with zero slack: delay any of these one day and the whole project finishes a day later. Everything else has float.">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {criticalItems.map((c, i) => (
                <span key={i}>
                  {i > 0 && ' → '}
                  {c.track.label} › {c.item.name}
                </span>
              ))}
              {schedule?.projectFinish && (
                <span> — finish {formatDate(schedule.projectFinish)}</span>
              )}
            </Typography>
          </Box>
        )}
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems="flex-start">
        <Paper variant="outlined" sx={{ p: 2, flex: 7, width: '100%' }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
            Tracks
          </Typography>
          {sortedTracks.length === 0 && (
            <Typography color="text.secondary" variant="body2">
              No tracks yet — add them in the Plan tab.
            </Typography>
          )}
          {sortedTracks.map((track) => (
            <TrackProgressBar key={track.id} track={track} />
          ))}
        </Paper>

        <Stack gap={2} sx={{ flex: 5, width: '100%' }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Delivery milestones
            </Typography>
            {deliveryMilestones.length === 0 && (
              <Typography color="text.secondary" variant="body2">
                No delivery milestones — add them in the Plan tab.
              </Typography>
            )}
            {deliveryMilestones.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Milestone</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deliveryMilestones.map((m) => {
                    const { date: msDate, movedFrom } = milestoneEffectiveDate(m);
                    return (
                    <TableRow key={m.id}>
                      <TableCell>{m.name}</TableCell>
                      <TableCell>
                        {formatDate(msDate)}
                        {movedFrom && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            moved from {formatDate(movedFrom)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={humanizeStatus(m.computedStatus ?? m.status)}
                          color={statusColor(m.computedStatus ?? m.status)}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Qualifications
            </Typography>
            {qualifications.length === 0 && (
              <Typography color="text.secondary" variant="body2">
                No qualification checklists.
              </Typography>
            )}
            <Stack gap={0.5}>
              {qualifications.map((q) => {
                const pct = qualificationPct(q);
                return (
                  <Stack
                    key={q.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ cursor: 'pointer', py: 0.5, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => navigate(`/projects/${projectId}/qualifications/${q.id}`)}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {q.type.toUpperCase()}
                    </Typography>
                    <Stack direction="row" gap={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {pct}%
                      </Typography>
                      <Chip
                        size="small"
                        label={humanizeStatus(q.status)}
                        color={statusColor(q.status)}
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Paper>
        </Stack>
      </Stack>
    </Stack>
  );
}
